import React, { useState, useEffect, useRef } from 'react';
import { getSpriteUrl } from '../logic/assetUtils';
import Settings from '../components/Settings';
import AnimatedTitle from '../components/AnimatedTitle';
import { FallingLeaves } from '../common/Effects';
import { getGsapModifiers, AnimatedBackground } from '../common/AnimatedBackgrounds';
import { gsap } from 'gsap';

export default function Home({ gameLogic, onStartGame }) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const animatedBackgroundRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtons(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (animatedBackgroundRef.current) {
      const imageRef = animatedBackgroundRef.current.imageRef;
      const modifiers = getGsapModifiers(animatedBackgroundRef);

      timelineRef.current = gsap.timeline();

      timelineRef.current.to(
        imageRef,
        {
          duration: 12,
          scale: 1.1,
          ease: 'power2.inOut',
          modifiers,
        },
        'start'
      );

      timelineRef.current.to(
        imageRef,
        {
          duration: 50,
          x: '-300px',
          ease: 'power1.inOut',
          repeat: -1,
          yoyo: true,
          modifiers,
        },
        'start+=2'
      );
    }

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  const handleStartGame = () => {
    setButtonsDisabled(true);
    setIsTransitioning(true);

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    setTimeout(() => {
      onStartGame();
    }, 1000);
  };

  return (
    <div className="w-screen h-screen relative overflow-hidden bg-[#1a1a2e]">
      {/* Background Layer - z-0 */}
      <div className="absolute inset-0 z-0">
        <AnimatedBackground
          ref={animatedBackgroundRef}
          imageSrc={getSpriteUrl('homePageBackground')}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1a1a2e]/40 to-[#1a1a2e]/90" />
      </div>

      {/* Atmospheric Layer - z-5 */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        <FallingLeaves color="#FFE4E1" count={30} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,228,225,0.08),transparent_70%)]" />
      </div>

      {/* Decorative Layer - z-10 */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#FFE4E1]/10 rounded-full filter blur-[180px] animate-pulse-slow" />
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-[#DDA0DD]/10 rounded-full filter blur-[180px] animate-pulse-slow-delayed" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#FFB6C1]/5 rounded-full filter blur-[200px] animate-pulse-medium" />
      </div>

      {!showSettings && (
        <>
          {/* Content Layer - z-20 */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-start pt-16 pointer-events-none">
            <div className="w-[400px] h-[200px]">
              <AnimatedTitle />
            </div>
          </div>

          {/* Interactive Layer - z-30 */}
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <div className="flex flex-col gap-4 mt-32">
              <button
                onClick={handleStartGame}
                disabled={buttonsDisabled}
                className={`relative group px-12 py-4 transition-all duration-700 w-[320px] 
                  bg-gradient-to-r from-[#FFE4E1]/10 to-[#DDA0DD]/10 backdrop-blur-sm 
                  border border-[#FFE4E1]/20 rounded-xl overflow-hidden
                  hover:bg-gradient-to-r hover:from-[#FFE4E1]/20 hover:to-[#DDA0DD]/20 
                  hover:border-[#FFE4E1]/30 hover:shadow-[0_0_30px_rgba(255,228,225,0.15)]
                  ${showButtons ? 'translate-x-0 opacity-100' : 'translate-x-[-100vw] opacity-0'}
                  ${buttonsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{
                  transitionDelay: '0ms',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FFE4E1]/15 to-[#DDA0DD]/15 blur-sm" />
                </div>
                <span className="relative font-['Cormorant_Garamond'] font-semibold text-2xl text-white/90 tracking-wider">
                  Begin Story
                </span>
              </button>

              {[
                { text: 'Continue', disabled: true },
                { text: 'Settings', action: () => setShowSettings(true) },
                { text: 'Exit', action: undefined }
              ].map((item, index) => (
                <button
                  key={item.text}
                  onClick={item.action}
                  disabled={buttonsDisabled || item.disabled}
                  className={`relative group px-12 py-4 transition-all duration-700 w-[320px]
                    bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden
                    hover:bg-white/10 hover:border-white/20 hover:shadow-[0_0_20px_rgba(255,228,225,0.1)]
                    ${item.disabled ? 'opacity-30 cursor-not-allowed' : ''}
                    ${showButtons ? 'translate-x-0 opacity-100' : 'translate-x-[-100vw] opacity-0'}
                    ${buttonsDisabled ? 'opacity-25 cursor-not-allowed' : ''}`}
                  style={{
                    transitionDelay: `${(index + 1) * 100}ms`,
                    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#FFE4E1]/10 to-[#DDA0DD]/10 blur-sm" />
                  </div>
                  <span className="relative font-['Montserrat'] font-medium text-xl text-white/80 tracking-wide">
                    {item.text}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Settings Modal Layer - z-40 */}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}

      {/* Transition Layer - z-50 */}
      {isTransitioning && (
        <div
          className="absolute inset-0 z-50 bg-white transition-all duration-1000 ease-in-out"
          style={{
            opacity: isTransitioning ? 1 : 0,
            filter: 'brightness(1.5) blur(20px)',
          }}
        />
      )}

      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.1; }
        }
        @keyframes pulse-slow-delayed {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.05; }
        }
        @keyframes pulse-medium {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.05; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 12s ease-in-out infinite;
        }
        .animate-pulse-slow-delayed {
          animation: pulse-slow-delayed 15s ease-in-out infinite;
        }
        .animate-pulse-medium {
          animation: pulse-medium 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
